/** localStorage keys shared with document/spreadsheet host pages (iframe). */
// v3: idle-timeout replace of interval autosave (force fresh defaults for all browsers).
export const EDITOR_AUTOSAVE_ENABLED_KEY = 'jarvis.editor.idleTimeout.enabled';
export const EDITOR_AUTOSAVE_INTERVAL_KEY = 'jarvis.editor.idleTimeout.seconds';

export const EDITOR_AUTOSAVE_INTERVAL_OPTIONS = [30, 60, 90, 120] as const;
export type EditorAutoSaveIntervalSeconds = (typeof EDITOR_AUTOSAVE_INTERVAL_OPTIONS)[number];

/** Idle timeout saves + releases the edit lock after inactivity. On by default. */
export const DEFAULT_EDITOR_AUTOSAVE_ENABLED = true;
export const DEFAULT_EDITOR_AUTOSAVE_INTERVAL_SECONDS: EditorAutoSaveIntervalSeconds = 30;

export const EDITOR_AUTOSAVE_PREFS_CHANGED = 'jarvis:editor-idle-timeout-prefs-changed';

export interface EditorAutoSavePrefs {
  enabled: boolean;
  intervalSeconds: EditorAutoSaveIntervalSeconds;
}

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined';
}

export function normalizeEditorAutoSaveIntervalSeconds(
  value: unknown
): EditorAutoSaveIntervalSeconds {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  if (EDITOR_AUTOSAVE_INTERVAL_OPTIONS.includes(n as EditorAutoSaveIntervalSeconds)) {
    return n as EditorAutoSaveIntervalSeconds;
  }
  return DEFAULT_EDITOR_AUTOSAVE_INTERVAL_SECONDS;
}

export function readEditorAutoSaveEnabled(): boolean {
  if (!storageAvailable()) return DEFAULT_EDITOR_AUTOSAVE_ENABLED;
  try {
    const raw = localStorage.getItem(EDITOR_AUTOSAVE_ENABLED_KEY);
    if (raw === null) return DEFAULT_EDITOR_AUTOSAVE_ENABLED;
    return raw !== 'false' && raw !== '0';
  } catch {
    return DEFAULT_EDITOR_AUTOSAVE_ENABLED;
  }
}

export function writeEditorAutoSaveEnabled(enabled: boolean): void {
  if (!storageAvailable()) return;
  try {
    localStorage.setItem(EDITOR_AUTOSAVE_ENABLED_KEY, enabled ? 'true' : 'false');
    dispatchEditorAutoSavePrefsChanged();
  } catch {
    /* ignore */
  }
}

export function readEditorAutoSaveIntervalSeconds(): EditorAutoSaveIntervalSeconds {
  if (!storageAvailable()) return DEFAULT_EDITOR_AUTOSAVE_INTERVAL_SECONDS;
  try {
    const raw = localStorage.getItem(EDITOR_AUTOSAVE_INTERVAL_KEY);
    if (raw === null) return DEFAULT_EDITOR_AUTOSAVE_INTERVAL_SECONDS;
    return normalizeEditorAutoSaveIntervalSeconds(raw);
  } catch {
    return DEFAULT_EDITOR_AUTOSAVE_INTERVAL_SECONDS;
  }
}

export function writeEditorAutoSaveIntervalSeconds(seconds: EditorAutoSaveIntervalSeconds): void {
  if (!storageAvailable()) return;
  const next = normalizeEditorAutoSaveIntervalSeconds(seconds);
  try {
    localStorage.setItem(EDITOR_AUTOSAVE_INTERVAL_KEY, String(next));
    dispatchEditorAutoSavePrefsChanged();
  } catch {
    /* ignore */
  }
}

export function readEditorAutoSavePrefs(): EditorAutoSavePrefs {
  return {
    enabled: readEditorAutoSaveEnabled(),
    intervalSeconds: readEditorAutoSaveIntervalSeconds()
  };
}

export function writeEditorAutoSavePrefs(prefs: EditorAutoSavePrefs): void {
  writeEditorAutoSaveEnabled(prefs.enabled);
  writeEditorAutoSaveIntervalSeconds(prefs.intervalSeconds);
}

export function editorAutoSaveCeilingMs(intervalSeconds?: number): number {
  const sec = normalizeEditorAutoSaveIntervalSeconds(
    intervalSeconds ?? readEditorAutoSaveIntervalSeconds()
  );
  return sec * 1000;
}

export function dispatchEditorAutoSavePrefsChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(EDITOR_AUTOSAVE_PREFS_CHANGED, { detail: readEditorAutoSavePrefs() })
    );
  } catch {
    /* ignore */
  }
}
